import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import backendUrl from "../Utils/backendUrl";

const SESSION_KEY = "user";
const TOKEN_KEY = "authToken";

/**
 * Read the cached session from sessionStorage synchronously.
 * Returns { isLoggedIn, usertype, userData } or null if nothing cached.
 */
function readCachedSession() {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed.isLoggedIn && parsed.userData) {
      return {
        isLoggedIn: true,
        usertype: parsed.userData.usertype,
        userData: parsed.userData,
      };
    }
  } catch {
    // corrupt cache – clear it
    sessionStorage.removeItem(SESSION_KEY);
  }
  return null;
}

/**
 * Verify the token with the backend and refresh the cached session.
 */
async function verifyWithBackend() {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) return { isLoggedIn: false, usertype: null };

  try {
    const response = await axios.get(`${backendUrl}login`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });

    if (response.data.success) {
      const user = response.data.user;
      // Refresh cached session so next refresh is instant
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ isLoggedIn: true, userData: user })
      );
      return { isLoggedIn: true, usertype: user.usertype, userData: user };
    }
    return { isLoggedIn: false, usertype: null };
  } catch (err) {
    console.error("Session verification error:", err);
    return { isLoggedIn: false, usertype: null };
  }
}

export default function ProtectedRoute({ allowedUserTypes = [], children }) {
  // Phase 1: read cache synchronously — prevents the white flash
  const cachedSession = useRef(readCachedSession());

  const [session, setSession] = useState(
    cachedSession.current ?? { isLoggedIn: null, usertype: null }
  );
  // If we already have a cached session, no loading needed (no blink)
  const [loading, setLoading] = useState(cachedSession.current === null);

  useEffect(() => {
    // Skip backend call if we already have a cached session
    if (cachedSession.current !== null) return;

    verifyWithBackend().then((sess) => {
      setSession(sess);
      setLoading(false);
    });
  }, []);

  // Only show spinner when there is truly no cached session (first ever load)
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#fff",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!session.isLoggedIn) return <Navigate to="/" replace />;

  // usertype 0 or 5 => super-admin, allow everything
  if (session.usertype === 0 || session.usertype === 5) return children;

  // Check specific allowed user types
  if (allowedUserTypes.length === 0 || allowedUserTypes.includes(session.usertype))
    return children;

  return <Navigate to="/dashboard" replace />;
}
